// Plain words for the dashboard's raw tokens.
//
// Every string this file translates comes from a CLOSED vocabulary written by
// the server — the action taxonomy (`@loom/shared/actions`), the session kind
// enum (`@loom/shared/session`), and the ledger's `outcome` token. They are
// stable identifiers, which is exactly why they are unreadable: `tool_use`,
// `lifecycle`, `unavailable:budget_exhausted`.
//
// TWO RULES, and the second is the one that matters.
//
//   1. A token this file KNOWS is rendered as a localized phrase.
//   2. A token it does NOT know is still rendered — humanized (underscores to
//      spaces) rather than swallowed or shown as "Unknown". These vocabularies
//      grow on the server without this file being touched, and an operator
//      reading a timeline needs to see the new token, not a placeholder that
//      hides it.
//
// And in both cases the EXACT token stays reachable as the cell's `title`. The
// person reading this page is usually holding a Cloud Logging query in their
// other hand, and `split_part(outcome, ':', 1)` does not match "Not available".

import type { Translator } from "../i18n/index.js";

/** `user_message` → `Message`. The floor under every mapping below. */
export const humanizeToken = (token: string): string => {
  const spaced = token.replace(/[_-]+/g, " ").trim();
  if (spaced.length === 0) return token;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// ---------------------------------------------------------------------------
// Session kind — what the conversation WAS
// ---------------------------------------------------------------------------

const SESSION_KINDS = new Set(["chat", "issue", "implement", "unknown"]);

/**
 * The localized name for a session's kind, or `null` when there is nothing
 * honest to say.
 *
 * `null` covers two different absences and deliberately renders the same for
 * both: `kind === null` (a projection row written before the column existed)
 * and an unrecognised value. Neither is `unknown`, which IS a real state — a
 * session that was opened and never took a turn — and has its own phrase.
 */
export const sessionKindLabel = (
  t: Translator,
  kind: string | null | undefined,
): string | null => {
  if (typeof kind !== "string" || !SESSION_KINDS.has(kind)) return null;
  return t(`analytics.session.kind.${kind}`);
};

// ---------------------------------------------------------------------------
// Action kind — the timeline's "Kind" column
// ---------------------------------------------------------------------------

const ACTION_KINDS = new Set([
  "user_message",
  "tool_use",
  "workflow",
  "lifecycle",
  "apply",
  "error",
]);

export const actionKindLabel = (t: Translator, kind: string): string =>
  ACTION_KINDS.has(kind)
    ? t(`analytics.action.kind.${kind}`)
    : humanizeToken(kind);

// ---------------------------------------------------------------------------
// Outcome — the ledger's `token` / `token:key=value,…` shape
// ---------------------------------------------------------------------------

/**
 * The head tokens worth naming. Everything else falls through to
 * `humanizeToken`, which is why this set can stay small and does not have to
 * chase the server's enums.
 */
const OUTCOME_HEADS = new Set([
  "ok",
  "error",
  "unavailable",
  "rejected",
  "terminal",
  "recoverable",
  "proposed",
  "no_split",
  "split_recommended",
  "single_ticket",
]);

export interface OutcomeLabel {
  /** What the cell shows. */
  readonly text: string;
  /** The exact stored token, for the cell's `title` — see the header. */
  readonly title: string;
  /** `ok` / `bad` / `neutral`, for the cell's colour. Derived from the HEAD
   *  only: `split_part(outcome, ':', 1)` is how the server's own aggregations
   *  read this column, and the UI has no business reading it differently. */
  readonly tone: "ok" | "bad" | "neutral";
}

const toneOf = (head: string): OutcomeLabel["tone"] => {
  if (head === "ok") return "ok";
  if (head === "error" || head === "terminal" || head === "unavailable") {
    return "bad";
  }
  return "neutral";
};

/**
 * Split `token[:key=value,…]` into a readable phrase. The detail half is
 * humanized but never translated — its keys and values are catalogue
 * identifiers the server mints, and a localized guess at one would be a
 * mistranslation of something that has no meaning outside the code.
 */
export const outcomeLabel = (
  t: Translator,
  outcome: string | null,
): OutcomeLabel | null => {
  if (outcome === null || outcome.length === 0) return null;
  const sep = outcome.indexOf(":");
  const head = sep === -1 ? outcome : outcome.slice(0, sep);
  const detail = sep === -1 ? "" : outcome.slice(sep + 1);
  const headText = OUTCOME_HEADS.has(head)
    ? t(`analytics.outcome.${head}`)
    : humanizeToken(head);
  return {
    text: detail.length > 0 ? `${headText} · ${humanizeToken(detail)}` : headText,
    title: outcome,
    tone: toneOf(head),
  };
};

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/**
 * A person, the way the dashboard should say it: their name, with their handle
 * beside it when both are known.
 *
 * Falls back to the numeric id, which is not a formatting nicety — the name is
 * resolved live on the viewer's own GitLab token and a miss is ordinary (a
 * deleted account, a cross-host row, an expired token). The id is what the
 * fact table actually holds and what an erasure request is keyed by, so it is
 * the honest fallback rather than "Unknown user".
 */
export const personLabel = (
  t: Translator,
  row: {
    readonly displayName?: string | null;
    readonly username?: string | null;
    readonly user: { readonly gitlabUserId: number };
  },
): { readonly name: string; readonly handle: string | null } => {
  const name = row.displayName ?? row.username ?? null;
  if (name === null) {
    return {
      name: t("analytics.cell.user", { id: row.user.gitlabUserId }),
      handle: null,
    };
  }
  return {
    name,
    handle: row.username && row.displayName ? `@${row.username}` : null,
  };
};

/** A project, the way the dashboard should say it: its path, else its id. */
export const projectLabel = (
  t: Translator,
  project: { readonly projectPath: string; readonly gitlabProjectId: number },
): string =>
  project.projectPath ||
  t("analytics.cell.project", { id: project.gitlabProjectId });

/**
 * A session, the way the dashboard should say it.
 *
 * `name` is what a person can hold onto — "Refinement #84", "Chat" — and
 * `ref` is the eight-character id prefix that stays beside it, because the id
 * is what every log query and every support conversation is actually keyed by.
 * Neither replaces the other.
 */
export const sessionLabel = (
  t: Translator,
  row: {
    readonly sessionId: string;
    readonly kind?: string | null;
    readonly issueIid?: number | null;
  },
): { readonly name: string | null; readonly ref: string } => {
  const kind = sessionKindLabel(t, row.kind);
  const ref = `${row.sessionId.slice(0, 8)}…`;
  if (kind === null) return { name: null, ref };
  return {
    name:
      typeof row.issueIid === "number" ? `${kind} #${String(row.issueIid)}` : kind,
    ref,
  };
};
